<template>
  <v-app-bar
    app
    clipped-left
    clipped-right
    flat
    dense
    dark
    color="secondary"
    :height="APP_HEADER_HEIGHT"
    class="app-header px-2 px-md-3"
  >
    <!-- Left: brand -->
    <div class="app-header__brand d-flex align-center flex-shrink-0">
      <router-link
        to="/"
        class="app-header__brand-link d-flex align-center justify-center text-decoration-none"
        aria-label="Tendertrace home"
      >
        <img
          src="@/assets/img/tendertrace_logo-white-pink-bars.png"
          alt=""
          class="app-header__brand-logo"
        />
      </router-link>
    </div>

    <v-spacer />

    <!-- Right: search + actions -->
    <div class="app-header__actions d-flex align-center flex-shrink-0">
      <div class="app-header__search">
        <slot name="search">
          <v-text-field
            dense
            outlined
            hide-details
            placeholder="Keyword search"
            prepend-inner-icon="mdi-magnify"
            class="app-header__search-field"
            background-color="#f5f6f8"
            @keyup.enter="onSearchSubmit"
            @input="searchQuery = $event"
          />
        </slot>
      </div>

      <v-btn
        text
        small
        class="app-header__whatsnew text-capitalize d-none d-md-flex"
        aria-label="What's New"
        @click="$emit('whats-new')"
      >
        <v-icon left small color="#FFE70A">mdi-star-four-points</v-icon>
        What's New
      </v-btn>

      <v-btn
        icon
        small
        class="d-md-none"
        aria-label="What's New"
        @click="$emit('whats-new')"
      >
        <v-icon small color="#FFE70A">mdi-star-four-points</v-icon>
      </v-btn>

      <v-btn
        rounded
        depressed
        small
        color="primary"
        class="app-header__tracey-btn text-capitalize d-none d-md-flex"
        @click="$emit('ask-tracey')"
      >
        <v-icon left small color="white">mdi-creation</v-icon>
        Ask Tracey
      </v-btn>

      <v-btn
        icon
        small
        class="d-md-none"
        aria-label="Ask Tracey"
        @click="$emit('ask-tracey')"
      >
        <v-icon small color="#FFE70A">mdi-creation</v-icon>
      </v-btn>

      <v-btn icon small aria-label="My profile" data-tutorial="profile" @click="$emit('open-profile')">
        <v-avatar v-if="userPicture" size="28">
          <img :src="userPicture" alt="User profile">
        </v-avatar>
        <v-avatar v-else size="28" color="rgba(255, 255, 255, 0.15)">
          <v-icon color="white" small>mdi-account-circle</v-icon>
        </v-avatar>
      </v-btn>
    </div>
  </v-app-bar>
</template>

<script>
import { APP_HEADER_HEIGHT } from './navigationDrawerLayout.js';

export default {
  name: 'AppHeader',

  props: {
    userPicture: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      APP_HEADER_HEIGHT,
      searchQuery: '',
    };
  },

  methods: {
    onSearchSubmit() {
      this.$emit('search', this.searchQuery);
    },
  },
};
</script>

<style scoped>
.app-header {
  --app-header-brand-height: 25px;

  background-color: #12253a !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  /* Above temporary navigation drawers and their scrim (advanced search, Tracey). */
  z-index: 7 !important;
}

.app-header >>> .v-toolbar__content {
  align-items: center;
  overflow: visible;
}

.app-header__brand {
  margin-left: -12px;
  height: var(--app-header-brand-height);
  flex-shrink: 0;
}

.app-header__brand-link {
  display: flex;
  align-items: center;
  height: var(--app-header-brand-height);
  width: auto;
  min-width: 0;
  max-width: min(340px, 38vw);
  padding: 0 2px;
}

.app-header__brand-logo {
  display: block;
  height: var(--app-header-brand-height);
  width: auto;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;
}

/* Uniform gap between search, Tracey, and profile. */
.app-header__actions {
  max-width: 100%;
  gap: 8px;
}

@media (min-width: 960px) {
  .app-header__actions {
    gap: 12px;
  }
}

.app-header__search {
  flex-shrink: 0;
  width: 280px;
  max-width: 40vw;
  overflow: visible;
}

@media (min-width: 960px) {
  .app-header__search {
    width: 360px;
    max-width: 360px;
  }
}

.app-header__search-field >>> .v-input__control {
  min-height: 32px !important;
}

.app-header__search-field >>> .v-input__slot {
  min-height: 32px !important;
  border-radius: 6px !important;
  align-items: center !important;
}

.app-header__search-field >>> .v-input__prepend-inner {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.app-header__search-field >>> input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  color: #12253a !important;
  caret-color: #12253a;
}

.app-header__search-field >>> input::placeholder {
  color: rgba(18, 37, 58, 0.55) !important;
  opacity: 1;
}

.app-header__search-field >>> .v-input__prepend-inner .v-icon {
  margin-top: 0 !important;
  color: rgba(18, 37, 58, 0.65) !important;
}

.app-header__search-field >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.app-header__search-field >>> fieldset {
  border-color: rgba(18, 37, 58, 0.12) !important;
}

.app-header__tracey-btn {
  flex-shrink: 0;
  letter-spacing: 0;
  font-weight: 600;
  font-size: 0.8125rem !important;
  height: 32px !important;
  white-space: nowrap;
}
</style>
